// MÀN KHOÁ TÍNH NĂNG CHƯA MUA — chiếm chỗ nội dung trang, thanh trái giữ nguyên.
//
// Sếp Phú chốt 15/09/2026: workspace chỉ dùng tính năng của gói đã mua ("mua CRM thì dùng CRM, mua
// chat đa kênh thì dùng chat đa kênh"); tính năng chưa mua bấm vào là hiện BẢNG GIÁ để mua ngay tại
// màn đó — cùng cách màn chào bán Hộp thư đã làm (components/inbox/InboxLicenseGate). Hết 14 ngày
// dùng thử mà chưa mua thì mọi tính năng đều rơi vào màn này.
//
// Dữ liệu = `user.module_access` do BE trả ở /auth/login · /auth/me · switch-tenant
// (cnvwork-saas api/src/services/moduleAccessService.js):
//   enforced     workspace có đang khoá theo gói không (false = workspace đời cũ, host không dựng màn này)
//   locked       mã module đang khoá
//   offers[code] gói bán được cho module đó, bậc thấp trước — { key, self_order }
//   trial_ends_at / trial_active   để nói đúng "đã hết dùng thử" thay vì "chưa mua"
//
// Host quyết định KHI NÀO dựng (theo đường dẫn), kernel chỉ vẽ. Hệ không gửi `module_access` (Core,
// Connect) thì host không gọi tới đây — kernel không tự đoán trạng thái khoá.
//
// `TrialExpiredScreen` (export riêng, cuối tệp) — màn TOÀN TRANG khi workspace hết dùng thử mà chưa từng mua
// (`module_access.trial_off`, sếp Phú 30/10/2026): BE chặn mọi request trừ /marketplace + /auth nên host dựng màn này
// THAY cả khung app, không menu.

import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import { Lock, Clock, Users, ArrowUpRight, RefreshCw, LogOut, Repeat } from 'lucide-react';
import { usePermission } from '../../hooks/usePermission.js';
import { useItemCopy } from '@host/marketplaceBrand';
// ⛔ NAMESPACE import cho cờ: `SELF_ORDER_ENABLED` chỉ hệ bán trực tuyến mới khai — named import một
// export mà hệ kia không có là VỠ BUILD hệ đó (xem MarketplacePage).
import * as hostBrand from '@host/marketplaceBrand';
import PlanPurchasePanel from '../../components/marketplace/PlanPurchasePanel.jsx';
import { PRODUCT_NAME } from '../../lib/hostBrand';
import SelfOrderPaymentModal from '../../components/marketplace/SelfOrderPaymentModal.jsx';
import { fmtDate } from '../../i18n/format.js';
import '../../i18n/c/pages-marketplace.js';

/** Tên + một dòng mô tả cho từng module khoá được. Mã khớp moduleAccessService.MODULES bên BE. */
function useModuleCopy() {
  const { t } = useLingui();
  return {
    crm: { name: t`CRM`, desc: t`Khách hàng, cơ hội bán hàng, báo giá, hoá đơn và báo cáo doanh số.` },
    hr: { name: t`Nhân sự & Chấm công`, desc: t`Hồ sơ nhân viên, hợp đồng, chấm công, nghỉ phép và tài sản.` },
    tasks: { name: t`Dự án & Công việc`, desc: t`Dự án, giao việc và lịch làm việc của cả đội.` },
    approvals: { name: t`Phê duyệt`, desc: t`Đơn từ và luồng duyệt nhiều cấp.` },
    support: { name: t`Hỗ trợ`, desc: t`Ticket hỗ trợ khách hàng và nội bộ.` },
    marketing: { name: t`Marketing`, desc: t`Chiến dịch, sự kiện, form thu lead và tệp quảng cáo.` },
    recruitment: { name: t`Tuyển dụng`, desc: t`Tin tuyển dụng, ứng viên và quy trình tuyển.` },
    automation: { name: t`Tự động hoá`, desc: t`Luồng tự động gửi thông báo, giao việc theo sự kiện.` },
  };
}

export default function ModuleLockScreen({
  moduleCode,
  access,
  billingPath = '/billing',
  seatSettingsPath = '/settings/feature-licenses',
}) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const copy = useModuleCopy()[moduleCode] || { name: moduleCode, desc: null };
  const moduleName = copy.name;

  const offers = access?.offers?.[moduleCode] || [];
  const buyable = hostBrand.SELF_ORDER_ENABLED ? offers.filter((o) => o.self_order) : [];
  const hasConsultOnly = offers.some((o) => !buyable.includes(o));
  const [pickedKey, setPickedKey] = useState(null);
  const active = buyable.find((o) => o.key === pickedKey) || buyable[0] || null;
  const canBuy = can('marketplace', 'create');
  // Đặt đơn xong mở POPUP tại chỗ — đá khách sang trang khác giữa lúc trả tiền là chỗ dễ bỏ cuộc nhất
  // (cùng quyết định sếp Phú chốt 28/08/2026 cho màn Hộp thư).
  const [orderId, setOrderId] = useState(null);

  // "Hết dùng thử" chỉ khi workspace từng dùng thử, hết hạn, và CHƯA mua gì mở module. Đã mua CRM Only mà
  // bấm vào Nhân sự thì câu đúng là "chưa có trong gói", không phải "hết dùng thử".
  const trialEnded = !!access?.trial_ends_at && !access?.trial_active && !(access?.unlocked?.length);
  const ownsOtherModules = (access?.unlocked?.length || 0) > 0;

  return (
    <div className="flex-1 min-h-0 h-full overflow-y-auto bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <LockGlyph icon={trialEnded ? Clock : Lock} />
          <h1 className="text-2xl font-bold text-gray-900">
            {trialEnded
              ? <Trans>Đã hết 14 ngày dùng thử</Trans>
              : <Trans>{moduleName} chưa có trong gói của workspace</Trans>}
          </h1>
          <p className="text-[15px] text-gray-500 mt-2 max-w-xl mx-auto leading-relaxed">
            {trialEnded
              ? <Trans>Dữ liệu của workspace vẫn còn nguyên. Chọn gói để tiếp tục dùng {moduleName}.</Trans>
              : <Trans>Workspace dùng được tính năng của gói đã mua. Mua gói có {moduleName} để mở ngay tại đây.</Trans>}
          </p>
          {copy.desc && <p className="text-[13px] text-gray-400 mt-1.5">{copy.desc}</p>}
        </div>

        {active ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6">
            {buyable.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4" role="tablist">
                {buyable.map((o) => (
                  <OfferTab
                    key={o.key}
                    offer={o}
                    active={active.key === o.key}
                    onClick={() => setPickedKey(o.key)}
                  />
                ))}
              </div>
            )}
            <OfferTagline offer={active} />
            {canBuy ? (
              <PlanPurchasePanel
                key={active.key}
                itemKey={active.key}
                onOrdered={(order) => setOrderId(order.id)}
              />
            ) : (
              // Người không có quyền mua vẫn được XEM giá — họ là người đi đề xuất với quản trị.
              <>
                <PlanPurchasePanel key={active.key} itemKey={active.key} onOrdered={() => {}} compact />
                <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-[13px] text-gray-600">
                  <Users className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <Trans>Bạn không có quyền đặt mua. Gửi bảng giá này cho quản trị workspace để họ đặt đơn.</Trans>
                </div>
              </>
            )}
            {ownsOtherModules && (
              <p className="mt-4 text-[12.5px] text-gray-500">
                <Trans>Mua thêm gói này không làm mất gói đang dùng. Cần cấn trừ phần còn lại khi nâng cấp thì liên hệ đội {PRODUCT_NAME}.</Trans>
              </p>
            )}
          </div>
        ) : (
          // Hệ không bán trực tuyến, hoặc chưa có gói nào tự mua được cho module này ⇒ không dựng khung mua
          // rỗng (đọc thành "hỏng"), đưa thẳng sang trang gói.
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate(billingPath)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold hover:from-pink-600 hover:to-rose-600"
            >
              <Trans>Xem các gói</Trans> <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12.5px] text-gray-500">
          <span><Trans>Thanh toán bằng chuyển khoản. Gói được mở ngay sau khi hệ thống nhận đủ tiền.</Trans></span>
          {active && (
            <button
              type="button"
              onClick={() => navigate(billingPath)}
              className="inline-flex items-center gap-1 text-blue-600 hover:underline"
            >
              <Trans>Xem tất cả gói</Trans> <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {hasConsultOnly && (
          <p className="text-center text-[12.5px] text-gray-400 mt-2">
            {/* Không hứa «không giới hạn»: Unlimited của Work chốt trần 250 user (07/10/2026), nền tảng
                khác có trần khác — câu chung chỉ nói «nhiều hơn», con số nằm ở trang Gói. */}
            <Trans>Cần nhiều người dùng hơn? Gói Unlimited được tư vấn riêng ở trang Gói & Thanh toán.</Trans>
          </p>
        )}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-500 hover:text-gray-700 text-[13px]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> <Trans>Đã thanh toán xong — tải lại</Trans>
          </button>
        </div>
      </div>

      <SelfOrderPaymentModal
        orderId={orderId}
        isOpen={!!orderId}
        onClose={() => setOrderId(null)}
        orderHref={orderId ? `/marketplace/orders/${orderId}` : null}
        onGoSeats={() => { setOrderId(null); navigate(seatSettingsPath); }}
      />
    </div>
  );
}

// Tên gói tra qua hook của host — tách thành component con để không gọi hook trong vòng lặp (Rules of Hooks).
function OfferTab({ offer, active, onClick }) {
  const { name } = useItemCopy({ key: offer.key });
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
        active
          ? 'border-pink-500 bg-pink-50 text-pink-700'
          : 'border-gray-200 text-gray-600 hover:border-gray-300'
      }`}
    >
      {name}
    </button>
  );
}

function OfferTagline({ offer }) {
  const { name, tagline } = useItemCopy({ key: offer.key });
  return (
    <div className="mb-4">
      <div className="text-[15px] font-semibold text-gray-900">{name}</div>
      {tagline && <div className="text-[13px] text-gray-500 mt-0.5">{tagline}</div>}
    </div>
  );
}

/** Ổ khoá (chưa mua) / đồng hồ (hết dùng thử) trên khối chuyển sắc — cùng ngôn ngữ hình với màn Hộp thư. */
function LockGlyph({ icon: Icon }) {
  return (
    <div className="relative w-[120px] h-[120px] mx-auto mb-3 flex items-center justify-center">
      <span className="absolute w-[120px] h-[120px] rounded-full bg-pink-500/[0.06]" />
      <span className="absolute w-[88px] h-[88px] rounded-full bg-pink-500/10" />
      <span className="relative w-[64px] h-[64px] rounded-[22px] bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg shadow-pink-500/30 flex items-center justify-center">
        <Icon className="w-7 h-7 text-white" />
      </span>
    </div>
  );
}

/**
 * Gói chào trên màn hết dùng thử: hợp mọi gói của mọi module (BE `offers[code]`), gói mở NHIỀU module đứng trước —
 * workspace vừa dùng thử cả bộ Work nên All-in-one là lựa chọn mặc định, CRM Only xếp sau.
 */
function workspaceOffers(access) {
  const byKey = new Map();
  for (const list of Object.values(access?.offers || {})) {
    for (const o of list || []) {
      const cur = byKey.get(o.key);
      byKey.set(o.key, { offer: cur?.offer || o, modules: (cur?.modules || 0) + 1 });
    }
  }
  return [...byKey.values()].sort((a, b) => b.modules - a.modules).map((x) => x.offer);
}

/**
 * MÀN TOÀN TRANG — workspace hết 14 ngày dùng thử mà chưa từng mua gói nào (`access.trial_off`). Sếp Phú 30/10/2026:
 * «Sau 14 ngày sẽ off tài khoản nhưng chưa xoá · Sau 30 ngày sẽ xoá để giải phóng dung lượng». Dữ liệu giữ tới
 * `access.purge_at` (hạn dùng thử + 30 ngày) rồi máy chủ xoá tệp + đóng workspace.
 *
 * Chỉ còn đúng những việc máy chủ còn cho: mua gói tại chỗ (/marketplace), tải lại sau khi trả tiền, đổi workspace,
 * đăng xuất. Host truyền `onLogout` (bản revoke phiên phía máy chủ) và `onSwitchWorkspace` (bỏ trống ⇒ ẩn nút).
 */
export function TrialExpiredScreen({ access, workspaceName, onLogout, onSwitchWorkspace }) {
  const { can } = usePermission();
  const offers = workspaceOffers(access);
  const buyable = hostBrand.SELF_ORDER_ENABLED ? offers.filter((o) => o.self_order) : [];
  const [pickedKey, setPickedKey] = useState(null);
  const active = buyable.find((o) => o.key === pickedKey) || buyable[0] || null;
  const canBuy = can('marketplace', 'create');
  const [orderId, setOrderId] = useState(null);
  const purgeDate = fmtDate(access?.purge_at, 'short', '');

  return (
    <div className="min-h-screen overflow-y-auto bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <LockGlyph icon={Clock} />
          <h1 className="text-2xl font-bold text-gray-900"><Trans>Đã hết 14 ngày dùng thử</Trans></h1>
          <p className="text-[15px] text-gray-500 mt-2 max-w-xl mx-auto leading-relaxed">
            {workspaceName
              ? <Trans>Workspace <b className="text-gray-700">{workspaceName}</b> đang tạm khoá. Mua gói để mở lại ngay — mọi dữ liệu vẫn còn nguyên.</Trans>
              : <Trans>Workspace đang tạm khoá. Mua gói để mở lại ngay — mọi dữ liệu vẫn còn nguyên.</Trans>}
          </p>
          {purgeDate && (
            <p className="mt-3 inline-block text-[13px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 max-w-xl leading-relaxed">
              <Trans>Dữ liệu được giữ tới hết ngày {purgeDate}. Sau ngày này toàn bộ tệp bị xoá vĩnh viễn và workspace đóng lại.</Trans>
            </p>
          )}
        </div>

        {active ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6">
            {buyable.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4" role="tablist">
                {buyable.map((o) => (
                  <OfferTab key={o.key} offer={o} active={active.key === o.key} onClick={() => setPickedKey(o.key)} />
                ))}
              </div>
            )}
            <OfferTagline offer={active} />
            {canBuy ? (
              <PlanPurchasePanel key={active.key} itemKey={active.key} onOrdered={(order) => setOrderId(order.id)} />
            ) : (
              <>
                <PlanPurchasePanel key={active.key} itemKey={active.key} onOrdered={() => {}} compact />
                <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-[13px] text-gray-600">
                  <Users className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <Trans>Bạn không có quyền đặt mua. Gửi bảng giá này cho quản trị workspace để họ đặt đơn.</Trans>
                </div>
              </>
            )}
          </div>
        ) : (
          <p className="text-center text-[14px] text-gray-500">
            <Trans>Liên hệ đội {PRODUCT_NAME} để mua gói và mở lại workspace.</Trans>
          </p>
        )}

        <div className="mt-6 text-center text-[12.5px] text-gray-500">
          <Trans>Thanh toán bằng chuyển khoản. Gói được mở ngay sau khi hệ thống nhận đủ tiền.</Trans>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-white text-[13px]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> <Trans>Đã thanh toán xong — tải lại</Trans>
          </button>
          {onSwitchWorkspace && (
            <button
              type="button"
              onClick={onSwitchWorkspace}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-white text-[13px]"
            >
              <Repeat className="w-3.5 h-3.5" /> <Trans>Đổi workspace</Trans>
            </button>
          )}
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-white text-[13px]"
            >
              <LogOut className="w-3.5 h-3.5" /> <Trans>Đăng xuất</Trans>
            </button>
          )}
        </div>
      </div>

      <SelfOrderPaymentModal
        orderId={orderId}
        isOpen={!!orderId}
        onClose={() => setOrderId(null)}
        orderHref={null}
        // Gói vừa kích hoạt ⇒ máy chủ hết `trial_off`; tải lại để host dựng lại cả app (rồi mới tới trang cấp chỗ).
        onGoSeats={() => window.location.reload()}
      />
    </div>
  );
}
